import React, { useState } from 'react';
import { useTranslation } from "react-i18next";
import backButtonIcon from '../assets/backIcon.svg';
import { ERROR, PENDING } from '../../../store/globalStatuses/globalStatuses';
import Loader from '../../Loader';
import { store } from 'react-notifications-component';

import styles from './confirmForm.module.scss';
import exclaimImg from 'assets/exclaim.png';

import kaspiImg from '../assets/kaspi.png';
import mastercardImg from '../assets/mastercard.png';
import visaImg from '../assets/visa.png';
import appleImg from '../assets/applePay.png';
import cashImg from '../assets/cash.png';
import googleImg from '../assets/googlePay.png';
import transactionsActions from "../../../store/transactionsStore/transactionsActions";
import { useDispatch } from "react-redux";
import Checkbox from "../../FormElements/Checkbox";

const paymentType = {
  'VISA': visaImg,
  'MASTERCARD': mastercardImg,
  'KASPI': kaspiImg,
  'APPLE_PAY': appleImg,
  'GOOGLE_PAY': googleImg,
  'CASH': cashImg
}

// Business IDs that require SMS code instead of WhatsApp code
const SMS_CODE_BUSINESS_IDS = ["2024707981702074368", "2024709367416229888", "2034216763209551872", "2037054083965128704"];

export default function ConfirmForm(props) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    setStep,
    amount_sum,
    bonuses,
    client_cashback,
    confirmPayment,
    payment_status,
    comment,
    payment,
    give_award,
    add_stamp,
    notRegistered,
    customer,
    type,
    stamp_count,
    increasedCashback,
    isSendedUseCashbackCode,
    isStandardBonus,
    setIsStandardBonus,
    useReducedCashback,
    setUseReducedCashback,
    whatsAppCode,
    setWhatsAppCode,
    selectedSubscription,
    isDiscountOrder,
    selectedTag,
    branchTags,
    info,
    userTags,
    selectedUserTagId,
  } = props;

  // Check if this business requires SMS code instead of WhatsApp code
  const isSmsCodeBusiness = info && info.id && SMS_CODE_BUSINESS_IDS.includes(info.id.toString());

  // Get selected discount tag info
  const selectedBranchTag = selectedTag && branchTags
    ? branchTags.find(t => t.id.toString() === selectedTag.toString())
    : null;

  // Get selected user tag info
  const selectedUserTag = selectedUserTagId && userTags
    ? userTags.find(t => t.id.toString() === selectedUserTagId.toString())
    : null;

  // Detect stamp usage mode (talon)
  const isStampUsageOrder = !!selectedSubscription;

  const reducedPct = Number(info?.configuration?.cashier_reduced_cashback_percent) || 0;
  const reducedEnabled = reducedPct > 0;
  const reducedCashback = Math.min(client_cashback || 0, reducedPct);

  // Use backend's /cashback response directly — it already returns the correct max percentage
  const effectiveCashback = reducedEnabled && useReducedCashback ? reducedCashback : client_cashback;

  // Calculate discount amount if discount order (use selectedUserTag if available, otherwise selectedBranchTag)
  const discountPercentage = isDiscountOrder
    ? (selectedUserTag ? selectedUserTag.bonus_percentage : (selectedBranchTag ? selectedBranchTag.bonus_percentage : 0))
    : 0;
  const discountAmount = isDiscountOrder ? Math.round((parseInt(amount_sum) * discountPercentage) / 100) : 0;

  // Get discount tag name for display
  const discountTagName = selectedUserTag ? selectedUserTag.name : (selectedBranchTag ? selectedBranchTag.name : '');
  const finalAmount = isDiscountOrder ? parseInt(amount_sum) - discountAmount : parseInt(amount_sum) - parseInt(bonuses || 0);
  const [error, setErrror] = useState(false);

  const renderWhatsAppField = () => {
    // For restricted businesses: show SMS code field when withdrawing bonuses or discount order
    if (isSmsCodeBusiness && (isDiscountOrder || bonuses > 0)) {
      return (
        <div>
          <p>{t('sms_code')}</p>
          <input
            placeholder={t('sms_code')}
            value={whatsAppCode}
            onChange={(e) => setWhatsAppCode(e.target.value)}
            style={error ? {border: '1px solid red'} : {}}
          />
        </div>
      );
    }

    // Original behavior for other businesses
    if (!customer.send_code_for_use_cashback || (bonuses <= 0 && !isDiscountOrder)) return null;

    return (
      <div>
        <p>{t('whatsapp_code')}</p>
        <input
          placeholder={"Whatsapp code"}
          value={whatsAppCode}
          onChange={(e) => setWhatsAppCode(e.target.value)}
          style={error ? {border: '1px solid red'} : {}}
        />
      </div>
    )
  }

  const onSubmit = () => {
    // For restricted businesses: require SMS code when withdrawing bonuses or discount order
    if (isSmsCodeBusiness && (isDiscountOrder || bonuses > 0) && isSendedUseCashbackCode && !whatsAppCode.length) {
      return setErrror(true);
    }

    if (!isSmsCodeBusiness && bonuses > 0 && !customer.send_code_for_use_cashback && isSendedUseCashbackCode && !whatsAppCode.length) {
      return setErrror(true);
    }

    if (!isSmsCodeBusiness && isDiscountOrder && isSendedUseCashbackCode && !whatsAppCode.length) {
      return setErrror(true);
    }

    confirmPayment();
  }

  const renderBonus = () => {
    if (reducedEnabled) {
      return (
        <div className={styles.default}>
          <p>{t('NSPayModal.bonuses')}:</p>
          <div>
            <span style={{color: '#73D389', display: "flex", alignItems: 'center', gap: "6px"}}>
              <Checkbox checked={!useReducedCashback} onChange={() => setUseReducedCashback(false)}/>
              <p style={{marginTop: "2px", fontSize: "15px"}}>
                {t("countOfStandardBonus", {
                  bonuses: (amount_sum - bonuses) * client_cashback / 100,
                  percent: client_cashback
                })}
              </p>
            </span>
            <span style={{color: '#73D389', display: "flex", alignItems: 'center', gap: "6px"}}>
              <Checkbox checked={useReducedCashback} onChange={() => setUseReducedCashback(true)}/>
              <p style={{marginTop: "2px", fontSize: "15px"}}>
                {t("countOfReducedBonus", {
                  bonuses: (amount_sum - bonuses) * reducedCashback / 100,
                  percent: reducedCashback
                })}
              </p>
            </span>
          </div>
        </div>
      )
    }

    if (!increasedCashback) {
      return (
        <div className={styles.default}>
          <p>{t('NSPayModal.bonuses')}:</p>
          <div>
          <span style={{color: '#73D389', display: "flex", alignItems: 'center', gap: "6px"}}>
            {(amount_sum - bonuses) * effectiveCashback / 100} {`(${effectiveCashback}%)`}
          </span>
          </div>
        </div>
      )
    }

    return (
      <div className={styles.default}>
        <p>{t('NSPayModal.bonuses')}:</p>
        <div>
          <span style={{color: '#73D389', display: "flex", alignItems: 'center', gap: "6px"}}>
            <Checkbox checked={isStandardBonus} onChange={() => setIsStandardBonus(true)}/>
            <p style={{marginTop: "2px", fontSize: "15px"}}>
              {t("countOfStandardBonus", {
                bonuses: (amount_sum - bonuses) * effectiveCashback / 100,
                percent: effectiveCashback
              })}
            </p>
          </span>
          <span style={{color: '#73D389', display: "flex", alignItems: 'center', gap: "6px"}}>
            <Checkbox checked={!isStandardBonus} onChange={() => setIsStandardBonus(false)}/>
            <p style={{marginTop: "2px", fontSize: "15px"}}>
              {t("countOfIncreasedBonus", {
                bonuses: (amount_sum - bonuses) * increasedCashback / 100,
                percent: increasedCashback
              })}
            </p>
          </span>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.backButton} onClick={() => setStep(2)}>
        <img src={backButtonIcon}/>
      </div>

      {/* Hide account amount when stamp usage order */}
      {!isStampUsageOrder && (
        <div className={styles.default}>
          <p>{t('NSPayment.account')}:</p>
          <span>{amount_sum}</span>
        </div>
      )}

      {/* Show discount info if discount order */}
      {isDiscountOrder && (selectedBranchTag || selectedUserTag) && (
        <div className={styles.default}>
          <p>Скидка ({discountTagName}):</p>
          <span style={{ color: '#4EB78B', fontWeight: '600' }}>
            -{discountAmount} ({discountPercentage}%)
          </span>
        </div>
      )}

      {/* Hide bonus fields when discount order or stamp usage order */}
      {!isDiscountOrder && !isStampUsageOrder && (
        <>
          <div className={styles.default}>
            <p>{t('NSPayment.takingBonus')}:</p>
            <span>{bonuses ? bonuses : 0}</span>
          </div>
        </>
      )}

      {type == 'phone' && stamp_count && (
        <div className={styles.default}>
          <p>{t('NSPayModal.stamps')}:</p>
          <span>{stamp_count}</span>
        </div>
      )}

      <div className={styles.default}>
        <p style={{ marginRight: '21px' }}>{t('comment')}:</p>
        <span style={{ display: 'block', maxWidth: '290px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{comment.length ? comment : '-'}</span>
      </div>
      {type == 'code' && stamp_count && (
        <div className={styles.default}>
          <p>{t('NSPayModal.stamps')}:</p>
          <span>{stamp_count}</span>
        </div>
      )}
      {selectedSubscription && (
        <div className={styles.default}>
          <p>Использование талона:</p>
          <span>{selectedSubscription.name}</span>
        </div>
      )}
      {selectedSubscription && (
        <div className={styles.default}>
          <p>Остаток талонов:</p>
          <span>{selectedSubscription.stamps_remaining} (из {selectedSubscription.total_stamps})</span>
        </div>
      )}

      {/* Hide bonus calculation when discount order or stamp usage order */}
      {!isDiscountOrder && !isStampUsageOrder && renderBonus()}

      <div className={styles.default}>
        <p>
          {give_award && `${t('NSPayModal.gift')}:`}
        </p>
        <span>
          {give_award && `${t('NSPayModal.given')}`}
        </span>
      </div>

      <div className={styles.marked}>
        <p>{t('NSClients.toSum')}</p>
        <span>{isStampUsageOrder ? '1 талон' : finalAmount}</span>
      </div>
      {
        notRegistered && (
          <div className={styles.notRegistered}>
            <img src={exclaimImg} />
            <div>
              <p>{t('NSClients.sms')}</p>
              <p>{t('NSClients.recommend')}</p>
            </div>
          </div>
        )
      }

      {renderWhatsAppField()}

      <button
        onClick={onSubmit}
        disabled={payment_status === PENDING}
        style={payment_status === PENDING ? {background: 'gray'} : {}}>
        {payment_status === PENDING ? <Loader/> : t('NSPayModal.confirmPayment')}
      </button>
      {payment_status === ERROR && !notRegistered && <div className={styles.errorMessage}>
        {/*<span><i style={{ marginRight: "5px" }}>✖</i><span>{t('NSPayModal.notActive')}</span></span>*/}
        <span><i style={{ marginRight: "5px" }}>✖</i><span>{t('Errors.error')}</span></span>
      </div>
      }
    </div>
  )
}
